import { useQuery } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { useAuthStore } from '../auth/authStore';
import { makeApi } from '../../lib/api';

/**
 * Phase 4 admin — side-by-side diff viewer for a Story's versions.
 *
 * Renders a 2-column comparison of any two versions, with added /
 * removed / changed fields highlighted. Opens as a modal-style
 * overlay above the StoryEditor.
 */
export interface DiffViewerProps {
  storyUuid: string;
  onClose(): void;
}

export function DiffViewer({ storyUuid, onClose }: DiffViewerProps): JSX.Element {
  const token = useAuthStore((s) => s.token);
  const spaceSlug = useAuthStore((s) => s.spaceSlug);

  const [fromVersion, setFromVersion] = useState<number | null>(null);
  const [toVersion, setToVersion] = useState<number | null>(null);

  // Pull the list of versions so the user can pick from / to.
  const versionsQuery = useQuery({
    enabled: token !== null && spaceSlug !== null,
    queryKey: ['versions', spaceSlug, storyUuid],
    queryFn: async () => {
      if (token === null || spaceSlug === null) throw new Error('no auth');
      const api = makeApi(token);
      const result = await api.listVersions(spaceSlug, storyUuid);
      if (result.status !== 'ok') throw new Error(`listVersions: ${result.status}`);
      // Prime the from/to selectors with the latest two versions.
      const versions = result.data.data;
      if (versions.length >= 2) {
        if (fromVersion === null) setFromVersion(versions[1]!.version_no);
        if (toVersion === null) setToVersion(versions[0]!.version_no);
      }
      return result.data;
    },
  });

  const diffQuery = useQuery({
    enabled:
      token !== null &&
      spaceSlug !== null &&
      fromVersion !== null &&
      toVersion !== null &&
      fromVersion !== toVersion,
    queryKey: ['diff', spaceSlug, storyUuid, fromVersion, toVersion],
    queryFn: async () => {
      if (token === null || spaceSlug === null) throw new Error('no auth');
      if (fromVersion === null || toVersion === null) throw new Error('no versions');
      const api = makeApi(token);
      const result = await api.diffVersions(spaceSlug, storyUuid, fromVersion, toVersion);
      if (result.status !== 'ok') throw new Error(`diff: ${result.status}`);
      return result.data.data;
    },
  });

  const versions = versionsQuery.data?.data ?? [];

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-full w-full max-w-5xl flex-col gap-4 overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
        <header className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-zinc-900">Version diff</h2>
          <div className="ml-auto flex items-center gap-2">
            <label className="text-xs text-zinc-600">from</label>
            <select
              value={fromVersion ?? ''}
              onChange={(e) => setFromVersion(Number(e.target.value))}
              className="rounded border border-zinc-300 px-2 py-1 text-xs"
            >
              {versions.map((v) => (
                <option key={v.id} value={v.version_no}>v{v.version_no}</option>
              ))}
            </select>
            <label className="text-xs text-zinc-600">to</label>
            <select
              value={toVersion ?? ''}
              onChange={(e) => setToVersion(Number(e.target.value))}
              className="rounded border border-zinc-300 px-2 py-1 text-xs"
            >
              {versions.map((v) => (
                <option key={v.id} value={v.version_no}>v{v.version_no}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-zinc-300 px-3 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
            >
              Close
            </button>
          </div>
        </header>

        {fromVersion === toVersion ? (
          <p className="text-sm text-zinc-500">Wähle zwei verschiedene Versionen zum Vergleichen.</p>
        ) : diffQuery.isPending ? (
          <p className="text-sm text-zinc-500">Lade Versionsvergleich…</p>
        ) : diffQuery.isError ? (
          <p role="alert" className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
            {(diffQuery.error as Error).message}
          </p>
        ) : (
          <DiffBody
            added={diffQuery.data?.added ?? []}
            removed={diffQuery.data?.removed ?? []}
            changed={diffQuery.data?.changed ?? []}
            unchanged={diffQuery.data?.unchanged ?? []}
          />
        )}
      </div>
    </div>
  );
}

function DiffBody({
  added,
  removed,
  changed,
  unchanged,
}: {
  added: string[];
  removed: string[];
  changed: Array<{ path: string; from: unknown; to: unknown }>;
  unchanged: string[];
}): JSX.Element {
  return (
    <div className="flex flex-col gap-4 text-sm">
      {changed.length > 0 ? (
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
            Changed ({changed.length})
          </h3>
          <ul className="mt-2 flex flex-col gap-2">
            {changed.map((c) => (
              <li key={c.path} className="rounded border border-amber-200 bg-amber-50 p-2 font-mono text-xs">
                <p className="font-semibold text-amber-900">{c.path}</p>
                <pre className="mt-1 whitespace-pre-wrap text-red-800">- {JSON.stringify(c.from)}</pre>
                <pre className="whitespace-pre-wrap text-green-800">+ {JSON.stringify(c.to)}</pre>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {added.length > 0 ? (
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
            Added ({added.length})
          </h3>
          <ul className="mt-1 list-disc pl-5 text-xs text-green-800">
            {added.map((k) => <li key={k} className="font-mono">{k}</li>)}
          </ul>
        </section>
      ) : null}
      {removed.length > 0 ? (
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
            Removed ({removed.length})
          </h3>
          <ul className="mt-1 list-disc pl-5 text-xs text-red-800">
            {removed.map((k) => <li key={k} className="font-mono">{k}</li>)}
          </ul>
        </section>
      ) : null}
      {unchanged.length > 0 ? (
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
            Unchanged ({unchanged.length})
          </h3>
          <ul className="mt-1 flex flex-wrap gap-1 text-xs text-zinc-500">
            {unchanged.map((k) => <li key={k} className="rounded bg-zinc-100 px-2 py-0.5 font-mono">{k}</li>)}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
